import type {RootState} from '@/store';
import {createSelector} from '@reduxjs/toolkit';
import type {AudioItemObject} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';
import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE, MIN_ITEM_DURATION} from '@PosterWhiteboard/items/item/item.types';
import {getItemPageEndTime} from '@PosterWhiteboard/items/item/item-timing.library';
import {getPlaybackDuration} from '@Libraries/audio-player/audio-player.library';
import {getCurrentPageTime} from '@Components/poster-editor/poster-editor-reducer';
import {getSelectedAudioTrackIds} from './timeline-group-selection';

interface SplittableTimelineBars {
  itemUids: string[];
  audioUids: string[];
}

interface SplitCandidate {
  uid: string;
  startTime: number;
  endTime: number;
  track: 'item' | 'audio';
}

const NO_ACTIVE_ITEM_IDS: string[] = [];

const isSpanSplittable = (startTime: number, endTime: number, splitTime: number): boolean => {
  return splitTime - startTime >= MIN_ITEM_DURATION && endTime - splitTime >= MIN_ITEM_DURATION;
};

const getActiveItemIds = (state: RootState): string[] => state.posterEditor.activeItemIds[state.posterEditor.currentPageId] ?? NO_ACTIVE_ITEM_IDS;

const getCurrentPageItemsHashMap = (state: RootState): Record<string, ItemObject> | undefined =>
  state.posterEditor.posterObject?.pages?.pagesHashMap?.[state.posterEditor.currentPageId]?.items?.itemsHashMap;

const getCurrentPageAudioItemsHashMap = (state: RootState): Record<string, AudioItemObject> | undefined =>
  state.posterEditor.posterObject?.pages?.pagesHashMap?.[state.posterEditor.currentPageId]?.audioClips?.audioItemsHashMap;

/**
 * The spans a split could cut, independent of where the playhead is: the selected canvas items
 * and audio clips, or with no selection every background (backgrounds tile the page, so at most
 * one of them contains any given time). Memoized on the selection and the page's items, so the
 * per-tick check below is a scan of a handful of spans rather than of every item on the page.
 */
export const getSplitCandidates = createSelector(
  [getActiveItemIds, getSelectedAudioTrackIds, getCurrentPageItemsHashMap, getCurrentPageAudioItemsHashMap],
  (activeItemIds, selectedAudioIds, itemsHashMap, audioItemsHashMap): SplitCandidate[] => {
    if (!itemsHashMap) {
      return [];
    }
    if (activeItemIds.length === 0 && selectedAudioIds.length === 0) {
      return Object.values(itemsHashMap)
        .filter((item) => item.isBackground)
        .map((item) => ({uid: item.uid, startTime: item.onPageStartTime, endTime: getItemPageEndTime(item), track: 'item'}));
    }
    const candidates: SplitCandidate[] = [];
    for (const uid of activeItemIds) {
      const itemObject = itemsHashMap[uid];
      if (!itemObject || itemObject.gitype === ITEM_TYPE.TRANSCRIPT || itemObject.gitype === ITEM_TYPE.SLIDESHOW) {
        continue;
      }
      candidates.push({uid, startTime: itemObject.onPageStartTime, endTime: getItemPageEndTime(itemObject), track: 'item'});
    }
    for (const uid of selectedAudioIds) {
      const audioObject = audioItemsHashMap?.[uid];
      if (!audioObject) {
        continue;
      }
      candidates.push({uid, startTime: audioObject.onPageStartTime, endTime: audioObject.onPageStartTime + getPlaybackDuration(audioObject.audioPlayer), track: 'audio'});
    }
    return candidates;
  }
);

/**
 * The bars a split at `splitTime` would cut — each candidate only when the playhead leaves at
 * least MIN_ITEM_DURATION on both sides. Transcripts and slideshows never split (a slideshow's
 * span is derived from its slides, so a generic span split can't cut it).
 */
export const getSplittableTimelineBars = (state: RootState, splitTime: number): SplittableTimelineBars => {
  const splittableBars: SplittableTimelineBars = {itemUids: [], audioUids: []};
  for (const candidate of getSplitCandidates(state)) {
    if (!isSpanSplittable(candidate.startTime, candidate.endTime, splitTime)) {
      continue;
    }
    if (candidate.track === 'item') {
      splittableBars.itemUids.push(candidate.uid);
    } else {
      splittableBars.audioUids.push(candidate.uid);
    }
  }
  return splittableBars;
};

/** Whether a split at `splitTime` would cut at least one bar — a selected one, or with no selection the background under the playhead. */
export const canSplitTimelineSelection = (state: RootState, splitTime: number): boolean => {
  const {itemUids, audioUids} = getSplittableTimelineBars(state, splitTime);
  return itemUids.length + audioUids.length > 0;
};

/**
 * Splits every splittable selected bar — canvas items, backgrounds, and audio clips —
 * at the current playhead, in ONE history entry. Bars the playhead misses stay
 * untouched; the selection stays on the left halves (originals keep their uids).
 */
export const splitTimelineSelection = async (): Promise<void> => {
  const page = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!page) {
    return;
  }
  const state = window.PMW.redux.store.getState();
  const splitTime = getCurrentPageTime(state);
  await page.splitTimelineBars({...getSplittableTimelineBars(state, splitTime), splitTime});
};
